const VOLLEDIG_CASCO_CODE = '2002';
const PURCHASE_WINDOW_MONTHS = 12;
const MAANDEN_BASIS = 12;
const MAANDEN_UPGRADE = 36;

export type GarantieType = 'NW' | 'AKW';

export interface GarantieDefinition {
  type: GarantieType;
  cgCode: string;                 // optional-coverage code that bumps the window to 36 maanden
  flagField: 'CA_NWEGAR' | 'CA_AKPGAR';
  maandenField: 'CA_NWEGMND' | 'CA_AKPGMND';
  dekkingToggle: 'Nieuwwaarderegeling' | 'Aanschafwaarderegeling';
  eersteEigenaar: boolean;        // required OB_EERSTEI ('J' => true, 'N' => false)
  maxWaarde: number;              // OB_VWACA incl. btw ceiling
  maxAgeYears?: number;           // vehicle-age ceiling, when the garantie has one
  windowDateField: 'OB_DATTOEL' | 'OB_AANKDAT';
}

export const GARANTIES: readonly GarantieDefinition[] = [
  {
    type: 'NW',
    cgCode: '2040',
    flagField: 'CA_NWEGAR',
    maandenField: 'CA_NWEGMND',
    dekkingToggle: 'Nieuwwaarderegeling',
    eersteEigenaar: true,
    maxWaarde: 105000,
    windowDateField: 'OB_DATTOEL',
  },
  {
    type: 'AKW',
    cgCode: '2041',
    flagField: 'CA_AKPGAR',
    maandenField: 'CA_AKPGMND',
    dekkingToggle: 'Aanschafwaarderegeling',
    eersteEigenaar: false,
    maxWaarde: 55000,
    maxAgeYears: 8,
    windowDateField: 'OB_AANKDAT',
  },
] as const;

export const getGarantieByCgCode = (cgCode: unknown): GarantieDefinition | undefined =>
  GARANTIES.find((g) => g.cgCode === String(cgCode));

const parseYmd = (value: unknown): Date | null => {
  const s = String(value ?? '');
  if (s.length !== 8) return null;
  const y = parseInt(s.slice(0, 4), 10);
  const m = parseInt(s.slice(4, 6), 10);
  const d = parseInt(s.slice(6, 8), 10);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const purchasedWithinWindow = (aankdat: unknown, now: Date): boolean => {
  const purchase = parseYmd(aankdat);
  if (!purchase) return false;
  const cutoff = new Date(now);
  cutoff.setMonth(cutoff.getMonth() - PURCHASE_WINDOW_MONTHS);
  return purchase >= cutoff && purchase <= now;
};

const vehicleAgeYears = (ob: any, now: Date): number => {
  const reg = parseYmd(ob?.OB_DATTOEL)
    ?? (ob?.OB_BOUWJR
      ? new Date(parseInt(String(ob.OB_BOUWJR), 10), (parseInt(String(ob.OB_BOUWMND || 1), 10) || 1) - 1, 1)
      : null);
  if (!reg) return Infinity;
  return (now.getTime() - reg.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
};

const passesSharedGates = (ob: any, isVolledigCasco: boolean): boolean =>
  !!ob
  && isVolledigCasco
  && ob.OB_IMPOBJ !== 'J';

const matchesDefinition = (def: GarantieDefinition, ob: any, now: Date): boolean => {
  if ((ob.OB_EERSTEI === 'J') !== def.eersteEigenaar) return false;
  if (Number(ob.OB_VWACA || 0) > def.maxWaarde) return false;
  if (def.maxAgeYears !== undefined && vehicleAgeYears(ob, now) > def.maxAgeYears) return false;
  if (!purchasedWithinWindow(ob[def.windowDateField], now)
      && !purchasedWithinWindow(ob.OB_DATTOEL, now)) return false;
  return true;
};

// The single garantie the vehicle qualifies for (owner type makes them mutually
// exclusive), or null. Drives both the CG push and the J/N flag.
export const getActiveGarantie = (ob: any, isVolledigCasco: boolean, now: Date = new Date()): GarantieDefinition | null => {
  if (!passesSharedGates(ob, isVolledigCasco)) return null;
  return GARANTIES.find((def) => matchesDefinition(def, ob, now)) ?? null;
};

export const requiresPurchaseDateForAkw = (
  ob: { OB_BOUWJR?: unknown; OB_BOUWMND?: unknown; OB_VWACA?: unknown } | undefined,
  now: Date = new Date(),
): boolean => {
  const bj = parseInt(String(ob?.OB_BOUWJR || '0'), 10);
  const bm = parseInt(String(ob?.OB_BOUWMND || '1'), 10);
  const ageMo = bj > 0 ? (now.getFullYear() - bj) * 12 + (now.getMonth() + 1 - bm) : 0;
  const vwaca = Number(ob?.OB_VWACA || 0);
  return bj > 0 && ageMo > 12 && ageMo <= 96 && vwaca <= 100000;
};

export const applyAutoGarantieFlags = (pp: any, now: Date = new Date()): void => {
  if (!pp) return;
  // The flags live on CA rows only. Every personenauto document carries the WA
  // (liability) section; a CA section exists on top of it only when beperkt or
  // volledig casco is selected. So no CA rows here means a WA-only policy: nothing
  // to flag, and never invent a CA row for it.
  if (!Array.isArray(pp.CA) || pp.CA.length === 0) return;

  const isVolledigCasco = pp.CA.some((ca: any) => String(ca?.CA_CODE) === VOLLEDIG_CASCO_CODE);
  const active = getActiveGarantie(pp.OB, isVolledigCasco, now);
  const cg: any[] = Array.isArray(pp.CG) ? pp.CG : [];
  const upgradeSelected = !!active && cg.some((e: any) => String(e?.CG_CODE) === active.cgCode);

  pp.CA.forEach((ca: any) => {
    GARANTIES.forEach((def) => {
      ca[def.flagField] = 'N';
      delete ca[def.maandenField];
    });
    if (active) {
      ca[active.flagField] = 'J';
      ca[active.maandenField] = upgradeSelected ? MAANDEN_UPGRADE : MAANDEN_BASIS;
    }
  });
};

export const applyCaDerivedFields = (pp: any, now: Date = new Date()): void => {
  if (!pp || !Array.isArray(pp.CA) || pp.CA.length === 0) return;
  const btwMvz = pp.OB?.OB_BTWCD === 'I' ? 'J' : 'N';
  pp.CA.forEach((ca: any) => {
    ca.CA_BTWMVZ = btwMvz;
  });
  applyAutoGarantieFlags(pp, now);
};
